import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useProducto } from '../../hooks/useProductos';
import { useAgregarAlCarrito } from '../../hooks/useCarrito';
import { PackageSelector } from '../../components/public/PackageSelector';
import { ZoomableImage } from '../../components/public/ZoomableImage';
import { Button } from '../../components/shared/Button';
import { Spinner } from '../../components/shared/Spinner';
import { CartIcon, CheckIcon, MinusIcon, PlusIcon } from '../../components/shared/icons';
import { useToast } from '../../components/shared/Toast';
import { estaAgotado } from '../../types/catalogo';
import { validarCantidadDePaquetes } from '../../validation/validators';

export function ProductoDetallePage() {
  const { id } = useParams<{ id: string }>();
  const { data: producto, isLoading } = useProducto(id ?? '');
  const agregarAlCarrito = useAgregarAlCarrito();
  const { mostrarError, mostrarExito } = useToast();

  const [paqueteId, setPaqueteId] = useState<string | undefined>();
  const [cantidad, setCantidad] = useState(1);
  const [indiceImagenSeleccionada, setIndiceImagenSeleccionada] = useState(0);

  // Al navegar entre productos (mismo componente, distinto id de ruta) hay que
  // volver a la primera imagen; si no, queda seleccionado un indice que puede
  // no existir en el nuevo producto.
  useEffect(() => {
    setIndiceImagenSeleccionada(0);
  }, [id]);

  if (isLoading) return <Spinner />;
  if (!producto) return <p className="px-4 sm:px-6 md:px-14 py-16 text-ink-muted">Producto no encontrado.</p>;

  const agotado = estaAgotado(producto);
  const paqueteActivo = paqueteId ?? producto.paquetes[0]?.id;
  const imagenPrincipal = producto.imagenes[indiceImagenSeleccionada] ?? producto.imagenes[0];

  const agregar = () => {
    if (!paqueteActivo) return;
    agregarAlCarrito.mutate(
      { productoId: producto.id, paqueteId: paqueteActivo, cantidadDePaquetes: cantidad },
      {
        onSuccess: () => mostrarExito('Producto agregado al carrito'),
        onError: (error) => mostrarError((error as Error).message),
      },
    );
  };

  return (
    <div className="max-w-[1280px] mx-auto">
      <div className="px-4 sm:px-6 md:px-14 pt-6 text-[13px] text-ink-muted flex gap-2 items-center flex-wrap">
        <Link to="/catalogo">Catálogo</Link>
        <span>/</span>
        <Link to={`/catalogo?categoriaId=${producto.categoriaId}`}>{producto.categoriaId}</Link>
        <span>/</span>
        <span className="text-ink">{producto.nombre}</span>
      </div>

      <section className="flex flex-col md:flex-row gap-8 md:gap-14 px-4 sm:px-6 md:px-14 py-6">
        <div className="flex-1 flex flex-col gap-3.5">
          <ZoomableImage
            src={imagenPrincipal}
            alt={producto.nombre}
            className="w-full aspect-[4/5] rounded-xl"
          />
          <div className="flex gap-3 flex-wrap">
            {producto.imagenes.slice(0, 4).map((img, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIndiceImagenSeleccionada(i)}
                aria-label={`Ver imagen ${i + 1} de ${producto.nombre}`}
                aria-pressed={i === indiceImagenSeleccionada}
                className={`w-[70px] h-[70px] sm:w-[90px] sm:h-[90px] rounded-lg bg-terracotta/15 shrink-0 bg-center bg-cover border-2 transition-colors ${
                  i === indiceImagenSeleccionada ? 'border-terracotta' : 'border-transparent'
                }`}
                style={{ backgroundImage: `url(${img})` }}
              />
            ))}
          </div>
        </div>

        <div className="flex-1 max-w-[520px] flex flex-col gap-6">
          <div className="flex flex-col gap-2.5">
            <div className="text-xs font-bold uppercase tracking-wide text-terracotta">
              {producto.categoriaId}
            </div>
            <h1 className="text-[34px] font-display font-semibold text-[oklch(28%_0.03_50)]">
              {producto.nombre}
            </h1>
            <p className="text-sm leading-relaxed text-ink-muted">{producto.descripcion}</p>
          </div>

          {agotado ? (
            <div className="text-sm font-semibold text-danger">Agotado por el momento</div>
          ) : (
            <div className="flex items-center gap-2 text-[13px] font-semibold text-success">
              <CheckIcon width={14} height={14} />
              {producto.stock} disponibles
            </div>
          )}

          <div className="flex flex-col gap-3">
            <div className="text-[13px] font-bold text-[oklch(28%_0.03_50)]">Elige tu paquete</div>
            <PackageSelector
              paquetes={producto.paquetes}
              paqueteSeleccionadoId={paqueteActivo}
              onSeleccionar={setPaqueteId}
            />
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center border-[1.5px] border-border rounded-lg">
              <button
                onClick={() => setCantidad((c) => Math.max(1, c - 1))}
                className="px-4 py-3 text-ink-muted"
                aria-label="Disminuir cantidad"
              >
                <MinusIcon width={14} height={14} />
              </button>
              <span className="px-5 py-3 text-sm font-semibold border-x border-border">{cantidad}</span>
              <button
                onClick={() => setCantidad((c) => c + 1)}
                className="px-4 py-3 text-ink-muted"
                aria-label="Aumentar cantidad"
              >
                <PlusIcon width={14} height={14} />
              </button>
            </div>
            <Button
              className="flex-1"
              disabled={agotado || !paqueteActivo || agregarAlCarrito.isPending || validarCantidadDePaquetes(cantidad) !== null}
              onClick={agregar}
              data-testid="add-to-cart-button"
            >
              <CartIcon width={18} height={18} />
              {agregarAlCarrito.isPending ? 'Agregando…' : 'Agregar al carrito'}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
